import React, { useState, useEffect } from 'react';
import { useDefaultAccount } from '../hooks/useDefaultAccount';
// 2026-06-06 source-of-truth-full-ui — connected viewer trumps
// the legacy default-account path for Account Equity / Buying
// Power / Daily P&L on the Risk Center metrics row.
import useAccountSummary from '../hooks/useAccountSummary';
import { useAccountIntel } from '../hooks/useAccountIntel';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  AlertTriangle, 
  TrendingDown, 
  TrendingUp, 
  PieChart, 
  BarChart3, 
  Activity, 
  Zap, 
  Info, 
  ChevronRight, 
  ArrowUpRight, 
  ArrowDownRight,
  Settings,
  Play,
  History,
  Target,
  Search,
  Filter,
  X
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { useAccountLiveData } from '../hooks/useAccountLiveData';
import OperatorDecisionPanels from './operator/OperatorDecisionPanels';

const RISK_MODELS = [
  { id: 'standard', name: 'Standard VaR', desc: '95% Confidence Interval' },
  { id: 'aggressive', name: 'Aggressive Growth', desc: 'Higher Drawdown Tolerance' },
  { id: 'conservative', name: 'Capital Preservation', desc: 'Strict Margin Controls' },
  { id: 'institutional', name: 'Institutional L/S', desc: 'Market Neutral Focus' },
];

// --- Components ---

const MetricCard = ({ label, value, change, trend, subValue, status }: any) => (
  <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 shadow-sm">
    <div className="flex justify-between items-start mb-2">
      <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">{label}</span>
      {status && (
        <span className={cn(
          "text-[9px] font-black px-1.5 py-0.5 rounded uppercase",
          status === 'Safe' ? "bg-emerald-500/10 text-emerald-600" :
          status === 'Moderate' ? "bg-amber-500/10 text-amber-600" :
          "bg-rose-500/10 text-rose-600"
        )}>
          {status}
        </span>
      )}
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-xl font-black text-[var(--app-text)] italic">{value}</span>
      {change && (
        <span className={cn(
          "text-[10px] font-bold flex items-center gap-0.5",
          trend === 'up' ? "text-emerald-600" : "text-rose-600"
        )}>
          {trend === 'up' ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
          {change}
        </span>
      )}
    </div>
    {subValue && <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mt-1">{subValue}</div>}
  </div>
);

const RiskAlert = ({ alert, onAction }: { alert: any, onAction: (id: number, action: string) => void }) => (
  <div className={cn(
    "p-4 rounded-xl border flex items-start gap-4 transition-all",
    alert.type === 'high' ? "bg-rose-500/5 border-rose-500/20" :
    alert.type === 'warning' ? "bg-amber-500/5 border-amber-500/20" :
    "bg-emerald-500/5 border-emerald-500/20"
  )}>
    <div className={cn(
      "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
      alert.type === 'high' ? "bg-rose-500/10 text-rose-600" :
      alert.type === 'warning' ? "bg-amber-500/10 text-amber-600" :
      "bg-emerald-500/10 text-emerald-600"
    )}>
      {alert.type === 'high' ? <ShieldCheck size={18} /> : 
       alert.type === 'warning' ? <AlertTriangle size={18} /> : 
       <ShieldCheck size={18} />}
    </div>
    <div className="flex-1">
      <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">{alert.title}</h4>
      <p className="text-[11px] text-[var(--muted-text)] font-medium mt-1 leading-relaxed">{alert.message}</p>
      <button 
        onClick={() => onAction(alert.id, alert.action)}
        className={cn(
          "mt-3 text-[9px] font-black uppercase italic px-3 py-1.5 rounded-lg transition-all",
          alert.type === 'high' ? "bg-rose-600 text-white hover:bg-rose-700" :
          alert.type === 'warning' ? "bg-amber-600 text-white hover:bg-amber-700" :
          "bg-[var(--app-text)] text-[var(--card-bg)] hover:opacity-90"
        )}
      >
        {alert.action}
      </button>
    </div>
  </div>
);

export default function RiskCenter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [exposureMode, setExposureMode] = useState<'Sector' | 'Asset Class' | 'Long/Short'>('Sector');
  const [riskModel, setRiskModel] = useState('standard');
  const [simulation, setSimulation] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [focusedSymbol, setFocusedSymbol] = useState<string | null>(null);
  const { account: liveAccount } = useDefaultAccount();
  const { riskAlerts: liveRiskAlerts, allocation: liveAllocation, health: liveHealth } = useAccountIntel(liveAccount?.id);
  React.useEffect(() => {
    if (liveRiskAlerts && liveRiskAlerts.data_status !== 'unavailable') {
      setAlerts((liveRiskAlerts.alerts || []).map((a: any) => ({
        id: a.id,
        type: a.type === 'high' ? 'high' : a.type === 'warning' ? 'warning' : 'safe',
        title: a.title,
        message: a.message,
        action: a.symbol ? `View ${a.symbol}` : 'Details',
      })));
    } else {
      setAlerts([]);
    }
  }, [liveRiskAlerts]);
  const { equity: liveEquity } = useAccountLiveData(liveAccount?.id, '1M');
  // 2026-06-06 source-of-truth-full-ui — viewer override for the
  // top metrics row when the user has an Alpaca paper row.
  const _sot = useAccountSummary();

  const livePositions = (liveAccount?.positions || []).map((p: any) => ({
    symbol: p.symbol,
    size: String(Math.abs(p.quantity ?? 0)),
    value: `$${Math.abs(p.market_value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
    entry: `$${(p.avg_entry_price ?? 0).toFixed(2)}`,
    current: `$${(p.current_price ?? 0).toFixed(2)}`,
    pnl: `${(p.unrealized_pnl ?? 0) >= 0 ? '+' : ''}$${(p.unrealized_pnl ?? 0).toFixed(2)}`,
    volatility: '—',
    weight: 'Live',
    exposure: `${Math.abs(((p.market_value ?? 0) / (liveAccount?.equity || 1)) * 100).toFixed(1)}%`,
  }));

  // Drill-in when search bar selects a symbol
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { value?: string } | undefined;
      if (!detail?.value) return;
      setFocusedSymbol(detail.value);
      // Auto-clear highlight after 4s so it doesn't persist forever
      window.setTimeout(() => setFocusedSymbol((cur) => (cur === detail.value ? null : cur)), 4000);
    };
    window.addEventListener('adversyn:symbol-selected', handler);
    return () => window.removeEventListener('adversyn:symbol-selected', handler);
  }, []);
  const [modalConfig, setModalConfig] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const [riskParams, setRiskParams] = useState({
    maxPosition: '10%',
    maxDailyLoss: '2%',
    maxSector: '30%',
    maxMargin: '50%',
    maxTrade: '$25k',
    volCap: '25%'
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleSimulate = async (scenario: string) => {
    logInteraction('RiskCenter', 'Run Scenario', { scenario });
    setIsSimulating(true);
    setSimulation(null);
    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSimulation(scenario);
    setIsSimulating(false);
  };

  const openModal = (config: any) => {
    logInteraction('RiskCenter', 'Open Modal', { title: config.title });
    setModalConfig(config);
    setIsModalOpen(true);
  };

  const handleAlertAction = (id: number, action: string) => {
    const alertItem = alerts.find(a => a.id === id);
    const symbol = alertItem?.message.match(/[A-Z]{3,4}/)?.[0] || 'NVDA';

    if (action === 'Reduce Position') {
      openModal({
        title: "Risk Mitigation: Reduce Position",
        type: "action",
        symbol: symbol,
        action: "sell",
        description: `Reducing ${symbol} exposure to bring portfolio concentration within institutional limits.`
      });
    } else if (action === 'View Details') {
      openModal({
        title: "Risk Breakdown",
        type: "details",
        alertId: id,
        data: alertItem
      });
    } else if (action === 'Adjust Risk') {
      openModal({
        title: "Risk Parameter Adjustment",
        type: "adjust",
        symbol: symbol
      });
    } else if (action === 'Settings') {
      openModal({
        title: "Margin & Risk Settings",
        type: "settings"
      });
    }
  };

  const handleApplyAdjustment = async () => {
    logInteraction('RiskCenter', 'Apply AI Adjustment');
    setIsProcessing('adjustment');
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsProcessing(null);
    setNotification({ message: 'AI Risk Adjustment applied successfully. Portfolio rebalanced.', type: 'success' });
  };

  const handleReviewStrategy = () => {
    openModal({
      title: "AI Strategy Review",
      type: "strategy",
      analysis: "Current market conditions suggest a rotation from high-beta tech into defensive staples. Your portfolio correlation is currently 0.82 with the NASDAQ, which is above your target of 0.65."
    });
  };

  const handleUpdateRiskParameters = async () => {
    logInteraction('RiskCenter', 'Update Risk Parameters');
    setIsProcessing('params');
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsProcessing(null);
    setNotification({ message: 'Risk parameters updated and persisted to institutional vault.', type: 'success' });
  };

  const handleResetDefaults = () => {
    logInteraction('RiskCenter', 'Reset Defaults');
    setRiskParams({
      maxPosition: '10%',
      maxDailyLoss: '2%',
      maxSector: '30%',
      maxMargin: '50%',
      maxTrade: '$25k',
      volCap: '25%'
    });
    setNotification({ message: 'Risk parameters reset to institutional defaults.', type: 'success' });
  };

  const handleTradingModeChange = (val: string) => {
    logInteraction('RiskCenter', 'Change Risk Model', { model: val });
    setRiskModel(val);
  };

  return (
    <div className="app-container space-y-8 pb-20">
      <div className="app-container space-y-6 pb-6 pt-6">
        <OperatorDecisionPanels />
      </div>
      {focusedSymbol && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs font-bold text-emerald-400 uppercase tracking-widest">
            Focused on {focusedSymbol} from search
          </div>
          <button
            onClick={() => setFocusedSymbol(null)}
            className="text-[10px] font-bold text-emerald-300 hover:text-emerald-100 uppercase"
          >Dismiss</button>
        </div>
      )}
      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div 
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 20, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className={cn(
              "fixed top-4 left-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 min-w-[300px]",
              notification.type === 'success' ? "bg-emerald-600 border-emerald-500 text-white" : "bg-rose-600 border-rose-500 text-white"
            )}
          >
            {notification.type === 'success' ? <ShieldCheck size={18} /> : <Info size={18} />}
            <span className="text-xs font-black uppercase italic tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Risk Management Center</h1>
          <p className="text-[var(--muted-text)] mt-1 font-medium italic">Centralized capital protection and exposure monitoring.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 mr-4 px-4 py-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl shadow-sm">
            <div className="text-right">
              <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest leading-none mb-1">Risk Model</div>
              <select
                data-testid="risk-select-model"
                value={riskModel}
                onChange={(e) => handleTradingModeChange(e.target.value)}
                className="bg-transparent border-none outline-none text-[10px] font-black text-emerald-600 uppercase italic cursor-pointer p-0 h-auto min-w-[140px]"
              >
                {RISK_MODELS.map(model => (
                  <option key={model.id} value={model.id} className="bg-[var(--card-bg)] text-[var(--app-text)]">{model.name}</option>
                ))}
              </select>
            </div>
            <Target size={16} className="text-emerald-600" />
          </div>
          <button
            data-testid="risk-btn-harden"
            onClick={() => {
              logInteraction('RiskCenter', 'Harden Account');
              setNotification({ message: 'Account hardening protocol initiated. Security layers increased.', type: 'success' });
            }}
            className="px-4 py-2 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic transition-all shadow-lg shadow-black/20 flex items-center gap-2 active:scale-95"
          >
            <ShieldCheck size={16} />
            Harden Account
          </button>
        </div>
      </div>

      {/* Top Risk Overview — live account data */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {liveAccount ? (
          <>
            <MetricCard label="Account Equity" value={_sot.brokerConnected ? _sot.display.equity : `$${(liveAccount.equity || liveAccount.account_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} change={_sot.brokerConnected ? _sot.display.dailyPl : (liveAccount.daily_pnl != null ? `$${Math.abs(liveAccount.daily_pnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : undefined)} trend={_sot.brokerConnected ? ((_sot.raw.dailyPl && parseFloat(_sot.raw.dailyPl) >= 0) ? 'up' : 'down') : ((liveAccount.daily_pnl ?? 0) >= 0 ? 'up' : 'down')} />
            <MetricCard label="Buying Power" value={_sot.brokerConnected ? _sot.display.buyingPower : `$${(liveAccount.buying_power || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} subValue={_sot.brokerConnected ? (_sot.syncStatus === 'synced' ? 'Available (Alpaca Paper)' : 'Sync pending') : "Available"} />
            <MetricCard label="Daily P&L" value={_sot.brokerConnected ? _sot.display.dailyPl : (liveAccount.daily_pnl != null ? `${(liveAccount.daily_pnl ?? 0) >= 0 ? '+' : ''}$${Math.abs(liveAccount.daily_pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A')} trend={_sot.brokerConnected ? ((_sot.raw.dailyPl && parseFloat(_sot.raw.dailyPl) >= 0) ? 'up' : 'down') : ((liveAccount.daily_pnl ?? 0) >= 0 ? 'up' : 'down')} />
            <MetricCard label="Open Positions" value={_sot.brokerConnected ? String(_sot.positions.length) : String(liveAccount.open_positions ?? (liveAccount.positions?.length ?? 0))} />
            {/* 2026-06-06 h-source-of-truth-purge — Daily P&L + Open Positions now consume /user/portfolio when the viewer has a broker. */}
            <MetricCard label="Portfolio Volatility" value="—" subValue="Provider not connected" />
            <MetricCard label="Max Drawdown" value={(() => { const pts = liveEquity?.points; if (!pts || pts.length < 2) return '—'; const eqs = pts.map(p => p.equity); const peak = Math.max(...eqs); const idx = eqs.indexOf(peak); const trough = idx >= 0 ? Math.min(...eqs.slice(idx)) : peak; return peak > 0 ? `${(((trough - peak) / peak) * 100).toFixed(1)}%` : '—'; })()} />
          </>
        ) : (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 animate-pulse">
              <div className="h-3 bg-[var(--card-border)] rounded mb-3 w-2/3" />
              <div className="h-6 bg-[var(--card-border)] rounded w-1/2" />
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-8">
        <div className="space-y-8">
          {/* Exposure Map */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <PieChart size={18} className="text-emerald-600" />
                <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Market Exposure Breakdown</h3>
              </div>
              <div className="flex bg-[var(--input-bg)] p-1 rounded-lg border border-[var(--input-border)]">
                {['Sector', 'Asset Class', 'Long/Short'].map(mode => (
                  <button 
                    key={mode} 
                    onClick={() => {
                      if (exposureMode === mode) return;
                      logInteraction('RiskCenter', 'Change Exposure Mode', { mode });
                      setExposureMode(mode as any);
                    }}
                    className={cn(
                      "px-3 py-1 text-[9px] font-black uppercase italic transition-all rounded-md border",
                      exposureMode === mode 
                        ? "bg-[var(--app-text)] text-[var(--card-bg)] border-[var(--app-text)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.2)]" 
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent"
                    )}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_250px] gap-8 items-center">
            <div className="relative min-h-[320px] h-[320px] w-full overflow-hidden">
              {isMounted && (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <RePieChart>
                    <Pie
                      data={exposureMode === 'Sector' ? (liveAllocation?.buckets?.map(b => ({ name: b.name, value: b.weight_pct, color: b.color })) || []) : []}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {(exposureMode === 'Sector' ? (liveAllocation?.buckets?.map(b => ({ color: b.color })) || []) : []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--app-text)', border: 'none', borderRadius: '12px', color: 'var(--card-bg)' }}
                      itemStyle={{ color: 'var(--card-bg)', fontSize: '12px', fontWeight: 'bold' }}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              )}
            </div>
              <div className="space-y-3">
                {(exposureMode === 'Sector' ? (liveAllocation?.buckets?.map(b => ({ name: b.name, value: b.weight_pct, color: b.color })) || []) : []).map((sector) => (
                  <div key={sector.name} className="flex items-center justify-between group cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: sector.color }} />
                      <span className="text-xs font-bold text-[var(--muted-text)] group-hover:text-[var(--app-text)] transition-colors">{sector.name}</span>
                    </div>
                    <span className="text-xs font-black text-[var(--app-text)] italic">{sector.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Position Risk Table */}
          <div data-testid="risk-panel-positions" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 size={18} className="text-emerald-600" />
                <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Position Risk Analysis</h3>
              </div>
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Search positions..."
                  onChange={(e) => logInteraction('RiskCenter', 'Search Positions', { query: e.target.value })}
                  className="bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl pl-9 pr-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none focus:border-emerald-500 transition-all w-64"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" size={14} />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[var(--card-header-bg)]">
                    {['Symbol', 'Size', 'Market Value', 'Unrealized P&L', 'Volatility', 'Risk Weight', 'Exposure'].map((header) => (
                      <th key={header} className="px-6 py-4 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest border-b border-[var(--card-border)]">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {livePositions.length === 0 ? (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">— No open positions —</td></tr>
                  ) : livePositions.map((pos, i) => (
                    <tr 
                      key={i} 
                      onClick={() => {
                        logInteraction('RiskCenter', 'View Position Risk', { symbol: pos.symbol });
                        openModal({
                          title: `Risk Analysis: ${pos.symbol}`,
                          type: "details",
                          data: pos
                        });
                      }}
                      className="hover:bg-[var(--input-bg)]/50 transition-colors cursor-pointer group border-b border-[var(--card-border)] last:border-0"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[var(--input-bg)] flex items-center justify-center text-xs font-black text-[var(--app-text)] italic group-hover:bg-emerald-500/10 group-hover:text-emerald-600 transition-all">
                            {pos.symbol[0]}
                          </div>
                          <span className="text-sm font-black text-[var(--app-text)] italic">{pos.symbol}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs font-bold text-[var(--muted-text)]">{pos.size}</td>
                      <td className="px-6 py-4 text-xs font-black text-[var(--app-text)] italic">{pos.value}</td>
                      <td className={cn(
                        "px-6 py-4 text-xs font-black italic",
                        pos.pnl.startsWith('+') ? "text-emerald-600" : "text-rose-600"
                      )}>
                        {pos.pnl}
                      </td>
                      <td className="px-6 py-4 text-xs font-bold text-[var(--muted-text)]">{pos.volatility}</td>
                      <td className="px-6 py-4">
                        <span className={cn(
                          "text-[9px] font-black px-2 py-0.5 rounded uppercase",
                          pos.weight === 'High' ? "bg-rose-500/10 text-rose-600" :
                          pos.weight === 'Medium' ? "bg-amber-500/10 text-amber-600" :
                          "bg-emerald-500/10 text-emerald-600"
                        )}>
                          {pos.weight}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs font-black text-[var(--app-text)] italic">{pos.exposure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="space-y-8">
          {/* Risk Alerts */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Zap size={18} className="text-emerald-600" />
              <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Active Risk Alerts</h3>
            </div>
            <div className="space-y-4">
              {alerts.map((alert) => (
                <RiskAlert key={alert.id} alert={alert} onAction={handleAlertAction} />
              ))}
            </div>
          </div>

          {/* AI Risk Advisor */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full -mr-16 -mt-16 blur-2xl" />
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-2 text-emerald-500">
                <Activity size={18} />
                <h3 className="text-xs font-black uppercase tracking-widest italic">AI Risk Advisor</h3>
              </div>
              <p className="text-xs text-[var(--app-text)] font-medium leading-relaxed italic opacity-90">
                {liveRiskAlerts?.alerts?.length
                  ? `${liveRiskAlerts.alerts[0].title}: ${liveRiskAlerts.alerts[0].message}`
                  : liveHealth?.reasons?.length
                  ? liveHealth.reasons[0].message
                  : 'No active risk alerts. Portfolio health rules are running.'}
              </p>
              <div className="space-y-2">
                <button 
                  onClick={handleApplyAdjustment}
                  disabled={isProcessing === 'adjustment'}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20 disabled:opacity-50"
                >
                  {isProcessing === 'adjustment' ? 'Applying...' : 'Apply Adjustment'}
                </button>
                <button 
                  onClick={handleReviewStrategy}
                  className="w-full py-2 bg-[var(--input-bg)] hover:bg-[var(--card-border)] text-[var(--app-text)] rounded-lg text-[10px] font-black uppercase italic transition-all border border-[var(--card-border)]"
                >
                  Review Strategy
                </button>
              </div>
            </div>
          </div>

          {/* Drawdown Monitoring */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TrendingDown size={18} className="text-rose-600" />
                <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Drawdown Tracker</h3>
              </div>
              <div className="text-right">
                <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase">Max DD</div>
                <div className="text-xs font-black text-rose-600 italic">{(() => { const pts = liveEquity?.points; if (!pts || pts.length < 2) return '—'; const eqs = pts.map(p => p.equity); const peak = Math.max(...eqs); const idx = eqs.indexOf(peak); const trough = idx >= 0 ? Math.min(...eqs.slice(idx)) : peak; return peak > 0 ? `${(((trough - peak) / peak) * 100).toFixed(1)}%` : '—'; })()}</div>
              </div>
            </div>
            <div className="relative min-h-[320px] h-[320px] w-full mb-4 overflow-hidden">
              {isMounted && (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <AreaChart data={(liveEquity?.points || []).map(p => ({ date: p.t.slice(0, 10), equity: p.equity }))}>
                    <defs>
                      <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="equity" stroke="#10b981" fillOpacity={1} fill="url(#colorEquity)" strokeWidth={2} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--app-text)', border: 'none', borderRadius: '12px', color: 'var(--card-bg)' }}
                      itemStyle={{ color: 'var(--card-bg)', fontSize: '10px', fontWeight: 'bold' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)]">
                <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase">Peak Equity</div>
                <div className="text-xs font-black text-[var(--app-text)] italic">{liveEquity?.points?.length ? `$${Math.max(...liveEquity.points.map(p => p.equity)).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</div>
              </div>
              <div className="p-3 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)]">
                <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase">Recovery</div>
                <div className="text-xs font-black text-emerald-600 italic">—</div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Correlation Risk */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-8">
            <Target size={18} className="text-emerald-600" />
            <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Portfolio Correlation Risk</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <tbody>
                <tr><td colSpan={6} className="p-8 text-center text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest italic">— Correlation data unavailable — provider not connected —</td></tr>
                {([] as any[][]).map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => {
                      const val = parseFloat(cell);
                      const isHeader = i === 0 || j === 0;
                      const isDiagonal = i === j && i !== 0;
                      const isHigh = !isHeader && !isDiagonal && val > 0.7;
                      const isMed = !isHeader && !isDiagonal && val > 0.4 && val <= 0.7;

                      return (
                        <td 
                          key={j} 
                          className={cn(
                            "p-3 text-center text-[10px] font-black uppercase transition-all",
                            isHeader ? "bg-[var(--card-header-bg)] text-[var(--muted-text)]" : "border border-[var(--card-border)]",
                            isHigh ? "bg-rose-500 text-white" :
                            isMed ? "bg-amber-400 text-white" :
                            !isHeader ? "bg-emerald-500/10 text-emerald-600" : ""
                          )}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-rose-500 rounded" />
              <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase">High (&gt;0.7)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-400 rounded" />
              <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase">Medium (0.4-0.7)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-500 rounded" />
              <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase">Low (&lt;0.4)</span>
            </div>
          </div>
        </div>

        {/* Risk Controls & Limits */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Settings size={18} className="text-emerald-600" />
              <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Account Risk Controls</h3>
            </div>
            <button
              data-testid="risk-btn-reset-defaults"
              onClick={() => {
                logInteraction('RiskCenter', 'Reset Defaults');
                // 2026-06-06 i-visible-defect-purge — /* alert removed by i-visible-defect-purge */ removed
              }}
              className="text-[10px] font-black text-emerald-600 uppercase italic hover:underline"
            >
              Reset Defaults
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { id: 'maxPosition', label: 'Max Position Size', value: riskParams.maxPosition, desc: 'Of total portfolio value' },
              { id: 'maxDailyLoss', label: 'Max Daily Loss', value: riskParams.maxDailyLoss, desc: 'Automatic liquidation threshold' },
              { id: 'maxSector', label: 'Max Sector Exposure', value: riskParams.maxSector, desc: 'Per individual sector' },
              { id: 'maxMargin', label: 'Max Margin Usage', value: riskParams.maxMargin, desc: 'Hard limit for new orders' },
              { id: 'maxTrade', label: 'Max Trade Size', value: riskParams.maxTrade, desc: 'Per single execution' },
              { id: 'volCap', label: 'Volatility Cap', value: riskParams.volCap, desc: 'Portfolio-wide annualized' },
            ].map((control) => (
              <div key={control.label} className="p-4 bg-[var(--input-bg)] rounded-2xl border border-[var(--input-border)] group hover:border-emerald-500/30 transition-all">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic">{control.label}</span>
                  <input 
                    type="text"
                    value={control.value}
                    onChange={(e) => setRiskParams(prev => ({ ...prev, [control.id]: e.target.value }))}
                    className="bg-transparent border-none text-right text-xs font-black text-emerald-600 italic w-16 outline-none"
                  />
                </div>
                <p className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{control.desc}</p>
                <div className="mt-3 h-1 bg-[var(--card-border)] rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 w-3/4" />
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="risk-btn-update-params"
            onClick={handleUpdateRiskParameters}
            disabled={isProcessing === 'params'}
            className="w-full mt-8 py-3 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic hover:opacity-90 transition-all disabled:opacity-50"
          >
            {isProcessing === 'params' ? 'Persisting Changes...' : 'Update Risk Parameters'}
          </button>
        </div>
      </div>

      {/* Scenario Simulator */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2rem] p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-1/3 space-y-6">
            <div className="flex items-center gap-2">
              <Play size={18} className="text-emerald-600" />
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic">Risk Scenario Simulator</h3>
            </div>
            <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed">
              Simulate extreme market events to understand potential portfolio impact and drawdown.
            </p>
            <div className="space-y-3">
              {[
                { id: 'crash', label: 'Market Crash (-10%)', icon: TrendingDown, color: 'text-rose-600 bg-rose-500/10' },
                { id: 'tech', label: 'Tech Sector Selloff (-15%)', icon: BarChart3, color: 'text-amber-600 bg-amber-500/10' },
                { id: 'vol', label: 'Volatility Spike (+50%)', icon: Activity, color: 'text-purple-600 bg-purple-500/10' },
                { id: 'rate', label: 'Interest Rate Shock', icon: Zap, color: 'text-blue-600 bg-blue-500/10' },
              ].map((scen) => (
                <button
                  key={scen.id}
                  data-testid={`risk-btn-scenario-${scen.id}`}
                  onClick={() => handleSimulate(scen.label)}
                  className={cn(
                    "w-full flex items-center justify-between p-4 rounded-2xl border transition-all",
                    simulation === scen.label ? "border-emerald-500 bg-emerald-500/5" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", scen.color)}>
                      <scen.icon size={16} />
                    </div>
                    <span className="text-xs font-black text-[var(--app-text)] uppercase italic">{scen.label}</span>
                  </div>
                  <ChevronRight size={16} className="text-[var(--muted-text)]" />
                </button>
              ))}
            </div>
          </div>

          <div data-testid="risk-panel-simulation" className="lg:w-2/3 bg-[var(--input-bg)] rounded-3xl p-8 border border-[var(--input-border)] flex flex-col items-center justify-center text-center relative overflow-hidden">
            {isSimulating && (
              <div className="absolute inset-0 bg-[var(--input-bg)]/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center space-y-4">
                <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                <div className="text-[10px] font-black text-[var(--app-text)] uppercase tracking-widest italic">Running Monte Carlo Simulation...</div>
              </div>
            )}
            
            {simulation ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full space-y-8"
              >
                <div className="space-y-2">
                  <div className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em]">Simulation Active</div>
                  <h4 className="text-xl font-black text-[var(--app-text)] uppercase italic">{simulation}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Projected Loss</div>
                    <div className="text-2xl font-black text-rose-600 italic">Illustrative</div>
                    <div className="text-[9px] text-[var(--muted-text)] italic">Estimate only — live Monte Carlo not connected</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Largest Position</div>
                    <div className="text-2xl font-black text-[var(--app-text)] italic">{livePositions[0]?.symbol || '—'}</div>
                    <div className="text-[9px] text-[var(--muted-text)] italic">By current market value</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Projected DD</div>
                    <div className="text-2xl font-black text-rose-600 italic">Illustrative</div>
                    <div className="text-[9px] text-[var(--muted-text)] italic">Estimate only — live Monte Carlo not connected</div>
                  </div>
                </div>

                <div className="p-6 bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] text-left">
                  <h5 className="text-[10px] font-black text-[var(--app-text)] uppercase italic mb-4">Impact Analysis</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-[var(--muted-text)]">Margin Call Probability</span>
                      <span className="text-xs font-black text-rose-600 italic">Low (12%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-[var(--muted-text)]">Liquidity Risk</span>
                      <span className="text-xs font-black text-amber-600 italic">Moderate</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-[var(--muted-text)]">Recovery Time (Est.)</span>
                      <span className="text-xs font-black text-[var(--app-text)] italic">14 Trading Days</span>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setSimulation(null)}
                  className="px-8 py-3 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic hover:opacity-90 transition-all"
                >
                  Reset Simulation
                </button>
              </motion.div>
            ) : (
              <div className="space-y-4 opacity-40">
                <div className="w-16 h-16 bg-[var(--card-border)] rounded-2xl flex items-center justify-center mx-auto">
                  <Activity size={32} className="text-[var(--muted-text)]" />
                </div>
                <div>
                  <h4 className="text-lg font-black text-[var(--app-text)] uppercase italic">No Active Simulation</h4>
                  <p className="text-sm text-[var(--muted-text)] font-medium">Select a scenario on the left to begin analysis.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Risk History Log */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <History size={18} className="text-emerald-600" />
          <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Risk Events History</h3>
        </div>
        <div className="space-y-3">
          <div className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest text-center py-8 italic">
            Risk event history will appear here as live alerts trigger over time.
          </div>
          {([] as any[]).map((event, i) => (
            <div 
              key={i} 
              onClick={() => {
                logInteraction('RiskCenter', 'View Risk Event', { title: event.title });
                openModal({
                  title: event.title,
                  type: "details",
                  data: event
                });
              }}
              className="flex items-center justify-between p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl hover:border-emerald-500/30 transition-all cursor-pointer group"
            >              <div className="flex items-center gap-4">
                <div className="text-[10px] font-black text-[var(--muted-text)] uppercase w-12">{event.date}</div>
                <div>
                  <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600 transition-colors">{event.title}</h4>
                  <p className="text-[10px] text-[var(--muted-text)] font-medium">{event.desc}</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-[var(--muted-text)] group-hover:text-[var(--app-text)] transition-all" />
            </div>
          ))}
        </div>
      </div>

      {/* Modal Simulation */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              data-testid="risk-modal"
              className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 max-w-md w-full shadow-2xl space-y-6"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-black text-white uppercase italic">{modalConfig?.title}</h3>
                <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              {modalConfig?.type === 'action' && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Symbol</div>
                    <div className="text-lg font-black text-white italic">{modalConfig?.symbol}</div>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Action</div>
                    <div className="text-lg font-black text-emerald-500 italic uppercase">{modalConfig?.action}</div>
                  </div>
                  <p className="text-xs text-slate-400 italic leading-relaxed">{modalConfig?.description}</p>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">Reduction Amount (%)</label>
                    <input 
                      type="range" 
                      className="w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer accent-emerald-500" 
                      defaultValue={50}
                    />
                    <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      /* alert removed by i-visible-defect-purge */
                      setIsModalOpen(false);
                    }}
                    className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20"
                  >
                    Confirm Execution
                  </button>
                </div>
              )}

              {modalConfig?.type === 'details' && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                    <h4 className="text-xs font-black text-white uppercase italic mb-2">{modalConfig.data.title}</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">{modalConfig.data.message}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                      <div className="text-[9px] font-black text-slate-500 uppercase">Current Level</div>
                      <div className="text-sm font-black text-rose-500 italic">42.5%</div>
                    </div>
                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                      <div className="text-[9px] font-black text-slate-500 uppercase">Threshold</div>
                      <div className="text-sm font-black text-white italic">30.0%</div>
                    </div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="w-full py-3 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase italic">Close Breakdown</button>
                </div>
              )}

              {modalConfig?.type === 'strategy' && (
                <div className="space-y-4">
                  <div className="p-6 bg-slate-950 border border-slate-800 rounded-2xl">
                    <div className="flex items-center gap-2 text-emerald-500 mb-4">
                      <Zap size={18} />
                      <span className="text-xs font-black uppercase italic tracking-widest">AI Analysis</span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed italic">{modalConfig.analysis}</p>
                  </div>
                  <div className="space-y-2">
                    <h5 className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Recommended Actions</h5>
                    <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl text-[10px] text-white font-bold uppercase italic">1. Hedge Tech Exposure with QQQ Puts</div>
                    <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl text-[10px] text-white font-bold uppercase italic">2. Increase Allocation to Healthcare (XLV)</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="w-full py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase italic">Acknowledge Strategy</button>
                </div>
              )}

              {modalConfig?.type === 'settings' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                      <span className="text-xs font-black text-white uppercase italic">Auto-Liquidation</span>
                      <div className="w-10 h-5 bg-emerald-600 rounded-full relative"><div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" /></div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                      <span className="text-xs font-black text-white uppercase italic">Margin Call Alerts</span>
                      <div className="w-10 h-5 bg-emerald-600 rounded-full relative"><div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" /></div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                      <span className="text-xs font-black text-white uppercase italic">Hard Leverage Cap</span>
                      <span className="text-xs font-black text-emerald-500 italic">4.0x</span>
                    </div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="w-full py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase italic">Save Settings</button>
                </div>
              )}

              {modalConfig?.type === 'adjust' && (
                <div className="space-y-6">
                  <p className="text-xs text-slate-400 italic">Adjusting risk parameters for {modalConfig.symbol} and related sector correlations.</p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Volatility Limit</label>
                      <input type="range" className="w-full accent-emerald-500" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Max Drawdown Stop</label>
                      <input type="range" className="w-full accent-rose-500" />
                    </div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="w-full py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase italic">Apply Changes</button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
